import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Alert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ForumIcon from "@mui/icons-material/Forum";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UndoIcon from "@mui/icons-material/Undo";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PrintIcon from "@mui/icons-material/Print";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import ReceituarioDialog from "../../components/ReceituarioDialog";
import SendToChatDialog from "../../components/SendToChatDialog";

const STATUS = {
  rascunho: { label: "Rascunho", color: "#607d8b" },
  gerado: { label: "Gerado", color: "#1976d2" },
  enviado: { label: "Aguardando aprovação", color: "#ed6c02" },
  aprovado: { label: "Arte aprovada", color: "#2e7d32" },
};

// Amostra da cor do PAPEL de cada modelo (o PDF é fundo branco; a cor vem do
// papel na impressora). Chave = campo `papel` de /receituarios/models.
const PAPEL_SWATCH = {
  amarelo: { bg: "#f5df4d", nome: "AMARELO" },
  azul: { bg: "#8ecae6", nome: "AZUL" },
  branco: { bg: "#ffffff", nome: "BRANCO" },
};

const Receituarios = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [sendTarget, setSendTarget] = useState(null);
  const [aprovarTarget, setAprovarTarget] = useState(null);
  // Confirmação antes de imprimir: cada modelo usa papel de outra cor — a pausa
  // dá tempo de trocar o papel na impressora e de escolher qual talão sai.
  const [printTarget, setPrintTarget] = useState(null);
  const [modelsInfo, setModelsInfo] = useState({});
  // Impressão em lote ("só as A do dia"): filtros + seleção → um PDF único.
  const [filtroModelo, setFiltroModelo] = useState("");
  const [filtroFila, setFiltroFila] = useState(false);
  const [filtroPeriodo, setFiltroPeriodo] = useState("todos");
  const [selecionados, setSelecionados] = useState([]);
  const [batchOpen, setBatchOpen] = useState(false);
  const [imprimindoLote, setImprimindoLote] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search };
      if (filtroModelo) params.modelo = filtroModelo;
      // "Fila de impressão" = aprovados que ainda não saíram da impressora.
      if (filtroFila) { params.status = "aprovado"; params.impresso = "nao"; }
      if (filtroPeriodo !== "todos") {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        if (filtroPeriodo === "7d") d.setDate(d.getDate() - 6);
        params.dataDe = d.toISOString();
      }
      const { data } = await api.get("/receituarios", { params });
      setRows(data.rows || []);
      // Ids selecionados podem ter saído do filtro — seleção sempre reflete a tela.
      setSelecionados([]);
    } catch (err) { toastError(err); }
    finally { setLoading(false); }
  }, [search, filtroModelo, filtroFila, filtroPeriodo]);

  // Dispara a impressão de um blob de PDF.
  // iframe em vez de window.open: bloqueador de pop-up não interfere.
  const printBlob = (blob) => {
    const url = URL.createObjectURL(blob);
    const frame = document.createElement("iframe");
    frame.style.display = "none";
    frame.src = url;
    frame.onload = () => {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    };
    document.body.appendChild(frame);
    setTimeout(() => { URL.revokeObjectURL(url); frame.remove(); }, 60000);
  };

  // O PDF numerado já está no servidor: imprimir não pode exigir gerar de novo,
  // porque cada geração consome numeração da faixa autorizada.
  const imprimir = async (r) => {
    try {
      const { data } = await api.get(`/receituarios/${r.id}/pdf`, { responseType: "blob" });
      printBlob(data);
      const { data: marc } = await api.post(`/receituarios/${r.id}/marcar-impresso`);
      setRows((prev) => prev.map((x) =>
        x.id === r.id ? { ...x, impressoPor: marc.impressoPor, impressoEm: marc.impressoEm } : x));
    } catch (err) { toastError(err); }
  };

  // Erros do print-batch chegam como Blob (responseType blob) — reconverte para
  // JSON para o toastError mostrar a mensagem traduzida, não "[object Blob]".
  const desblobarErro = async (err) => {
    if (err?.response?.data instanceof Blob) {
      try { err.response.data = JSON.parse(await err.response.data.text()); } catch { /* mantém */ }
    }
    return err;
  };

  const imprimirLote = async () => {
    setImprimindoLote(true);
    try {
      const { data } = await api.post("/receituarios/print-batch",
        { ids: selecionados }, { responseType: "blob" });
      printBlob(data);
      // Registro de quem imprimiu, um a um (endpoint existente).
      for (const id of selecionados) {
        try { await api.post(`/receituarios/${id}/marcar-impresso`); } catch { /* secundário */ }
      }
      toast.success(`Lote de ${selecionados.length} talão(ões) enviado para impressão.`);
      setBatchOpen(false);
      load();
    } catch (err) { toastError(await desblobarErro(err)); }
    finally { setImprimindoLote(false); }
  };

  useEffect(() => {
    api.get("/receituarios/models")
      .then(({ data }) => {
        const arr = Array.isArray(data) ? data : [];
        setLabels(Object.fromEntries(arr.map((m) => [m.key, m.label])));
        setModelsInfo(Object.fromEntries(arr.map((m) => [m.key, m])));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const openNew = () => { setEditingId(null); setDialogOpen(true); };
  const openEdit = (id) => { setEditingId(id); setDialogOpen(true); };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir este receituário?")) return;
    try {
      await api.delete(`/receituarios/${id}`);
      toast.success("Excluído.");
      load();
    } catch (err) { toastError(err); }
  };

  // Só entra no lote quem tem PDF gerado e arte aprovada.
  const selecionaveis = rows.filter((r) => r.arquivoFinalFileName && r.arteAprovada);
  const toggleSelecionado = (id) => setSelecionados((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const recsSelecionados = rows.filter((r) => selecionados.includes(r.id));
  const papeisDoLote = [...new Set(recsSelecionados.map((r) => modelsInfo[r.modelo]?.papel || "branco"))];
  const formatosDoLote = [...new Set(recsSelecionados.map((r) => r.formatoSaida))];

  const abrirLote = () => {
    if (papeisDoLote.length > 1) {
      toast.warn("O lote mistura cores de papel. Imprima uma cor por vez — use o filtro de modelo.");
      return;
    }
    if (formatosDoLote.length > 1) {
      toast.warn("O lote mistura formatos (tira e A4). Imprima um formato por vez.");
      return;
    }
    setBatchOpen(true);
  };

  return (
    <Paper style={{ margin: 16, padding: 16 }} variant="outlined">
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
        <DescriptionIcon color="primary" />
        <Typography variant="h6" style={{ flex: 1 }}>Receituários</Typography>
        <TextField
          size="small" placeholder="Buscar nº / prescritor"
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
        />
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>Novo</Button>
      </div>

      {/* Filtros da fila de impressão: "só as A do dia" em dois cliques. */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <FormControl size="small" style={{ minWidth: 220 }}>
          <InputLabel>Modelo</InputLabel>
          <Select value={filtroModelo} label="Modelo" onChange={(e) => setFiltroModelo(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {Object.values(modelsInfo).map((m) => (
              <MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" style={{ minWidth: 140 }}>
          <InputLabel>Período</InputLabel>
          <Select value={filtroPeriodo} label="Período" onChange={(e) => setFiltroPeriodo(e.target.value)}>
            <MenuItem value="todos">Todos</MenuItem>
            <MenuItem value="hoje">Hoje</MenuItem>
            <MenuItem value="7d">Últimos 7 dias</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Checkbox size="small" checked={filtroFila} onChange={(e) => setFiltroFila(e.target.checked)} />}
          label="Só aprovados não impressos"
        />
        <span style={{ flex: 1 }} />
        <Button variant="outlined" startIcon={<PrintIcon />}
          disabled={selecionados.length === 0}
          onClick={abrirLote}>
          Imprimir lote ({selecionados.length})
        </Button>
      </div>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Tooltip title="Selecionar todos os aprovados com PDF">
                <Checkbox size="small"
                  checked={selecionaveis.length > 0 && selecionados.length === selecionaveis.length}
                  indeterminate={selecionados.length > 0 && selecionados.length < selecionaveis.length}
                  onChange={(e) => setSelecionados(e.target.checked ? selecionaveis.map((r) => r.id) : [])}
                />
              </Tooltip>
            </TableCell>
            <TableCell>Nº</TableCell>
            <TableCell>Modelo</TableCell>
            <TableCell>Prescritor</TableCell>
            <TableCell>Atendimento</TableCell>
            <TableCell align="center">Folhas</TableCell>
            <TableCell align="center">Numeração</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell align="center">Impresso</TableCell>
            <TableCell align="right">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && (
            <TableRow><TableCell colSpan={10} align="center"><CircularProgress size={22} /></TableCell></TableRow>
          )}
          {!loading && rows.length === 0 && (
            <TableRow><TableCell colSpan={10} align="center"><Typography variant="body2" color="textSecondary">Nenhum receituário.</Typography></TableCell></TableRow>
          )}
          {rows.map((r) => {
            const st = STATUS[r.status] || STATUS.rascunho;
            const faixa = r.numeroInicialTexto && r.numeroFinalTexto
              ? `${r.numeroInicialTexto} – ${r.numeroFinalTexto}`
              : "—";
            const selecionavel = !!(r.arquivoFinalFileName && r.arteAprovada);
            return (
              <TableRow key={r.id} hover selected={selecionados.includes(r.id)}>
                <TableCell padding="checkbox">
                  <Tooltip title={selecionavel ? "Incluir no lote de impressão" : "Só aprovados com PDF entram no lote"}>
                    <span>
                      <Checkbox size="small" disabled={!selecionavel}
                        checked={selecionados.includes(r.id)}
                        onChange={() => toggleSelecionado(r.id)} />
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell>{r.numero}</TableCell>
                <TableCell>{labels[r.modelo] || r.modelo}</TableCell>
                <TableCell>
                  {r.emitenteNome || "—"}
                  {r.emitenteConselho && <Typography variant="caption" display="block" color="textSecondary">{r.emitenteConselho}</Typography>}
                </TableCell>
                <TableCell>
                  {r.ticketId ? (
                    <Tooltip title="Ir ao atendimento vinculado">
                      <span
                        onClick={() => navigate(`/tickets/${r.ticketId}`)}
                        style={{ cursor: "pointer", color: "#00897b", display: "inline-flex", alignItems: "center", gap: 4 }}
                      >
                        <ForumIcon fontSize="small" />
                        {r.contact?.name || `#${r.ticketId}`}
                      </span>
                    </Tooltip>
                  ) : (
                    <Typography variant="caption" color="textSecondary">—</Typography>
                  )}
                </TableCell>
                <TableCell align="center">{r.quantidade}</TableCell>
                <TableCell align="center"><code style={{ fontSize: 11 }}>{faixa}</code></TableCell>
                <TableCell align="center">
                  <Chip size="small" label={st.label} style={{ backgroundColor: st.color, color: "#fff" }} />
                  {r.arteAprovadaAuto && (
                    <Tooltip title={r.arteObservacao || "Aprovado pela resposta do cliente no chat"}>
                      <SmartToyIcon fontSize="small" style={{ color: "#2e7d32", marginLeft: 4, verticalAlign: "middle" }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="center">
                  {r.impressoPor?.name ? (
                    <Tooltip title={`${r.impressoPor.name}${r.impressoEm ? " em " + new Date(r.impressoEm).toLocaleString("pt-BR") : ""}`}>
                      <Chip size="small" icon={<PrintIcon style={{ color: "#fff" }} fontSize="small" />}
                        label="Impresso" style={{ backgroundColor: "#00695c", color: "#fff" }} />
                    </Tooltip>
                  ) : (
                    <Chip size="small" variant="outlined" label="Não impresso" />
                  )}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar / gerar"><IconButton size="small" onClick={() => openEdit(r.id)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  {r.status === "aprovado" && (
                    <Tooltip title="Desfazer aprovação (o cliente pediu edição)">
                      <IconButton size="small" style={{ color: "#ed6c02" }}
                        onClick={async () => {
                          if (!window.confirm(`Desfazer a aprovação de ${r.numero}? Ele volta para "aguardando aprovação".`)) return;
                          try {
                            await api.post(`/receituarios/${r.id}/aprovar-arte`, { aprovada: false });
                            toast.info("Aprovação desfeita. Ajuste a arte e envie nova prova.");
                            load();
                          } catch (err) { toastError(err); }
                        }}>
                        <UndoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {r.status === "enviado" && (
                    <Tooltip title="Registrar aprovação do cliente">
                      <IconButton size="small" style={{ color: "#2e7d32" }} onClick={() => setAprovarTarget(r)}>
                        <CheckCircleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={
                    !r.arquivoFinalFileName ? "Gere o PDF antes de imprimir"
                      : !r.arteAprovada ? "A arte precisa ser aprovada pelo cliente antes de imprimir"
                        : "Imprimir (usa o PDF já gerado)"
                  }>
                    <span>
                      <IconButton size="small"
                        disabled={!r.arquivoFinalFileName || !r.arteAprovada}
                        onClick={() => setPrintTarget(r)}>
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Enviar prova ao chat">
                    <span>
                      <IconButton size="small" disabled={r.status === "rascunho"} onClick={() => setSendTarget(r)}>
                        <ForumIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Excluir"><IconButton size="small" onClick={() => handleDelete(r.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <ReceituarioDialog
        open={dialogOpen}
        receituarioId={editingId}
        onClose={() => setDialogOpen(false)}
        onSaved={load}
      />

      {/* Registrar aprovação sem precisar passar pelo botão Imprimir. */}
      {aprovarTarget && (
        <Dialog open onClose={() => setAprovarTarget(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Confirmar aprovação da arte</DialogTitle>
          <DialogContent dividers>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Confirme que <b>o cliente aprovou esta arte</b>. O talão é documento controlado —
              regerar depois invalida a aprovação.
            </Alert>
            <Typography variant="body2">
              <b>{aprovarTarget.numero}</b> · {aprovarTarget.emitenteNome || "sem prescritor"} ·{" "}
              {aprovarTarget.quantidade} folha(s)
              <br />
              Faixa: <code>{aprovarTarget.numeroInicialTexto} a {aprovarTarget.numeroFinalTexto}</code>
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAprovarTarget(null)}>Cancelar</Button>
            <Button variant="contained" color="success" startIcon={<CheckCircleIcon />}
              onClick={async () => {
                try {
                  await api.post(`/receituarios/${aprovarTarget.id}/aprovar-arte`, { aprovada: true });
                  toast.success("Aprovação registrada. Liberado para impressão.");
                  setAprovarTarget(null);
                  load();
                } catch (err) { toastError(err); }
              }}>
              Cliente aprovou
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Lote: um PDF único, uma carga de papel, um diálogo de impressão. */}
      {batchOpen && (
        <Dialog open onClose={() => setBatchOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Imprimir lote — confira o papel</DialogTitle>
          <DialogContent dividers>
            {(() => {
              const sw = PAPEL_SWATCH[papeisDoLote[0]] || PAPEL_SWATCH.branco;
              const folhas = recsSelecionados.reduce((s, r) => s + (r.quantidade || 0), 0);
              return (
                <div style={{ textAlign: "center" }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <b>{recsSelecionados.length} talão(ões)</b> — {folhas} folha(s) no total
                  </Typography>
                  <div style={{ maxHeight: 140, overflowY: "auto", marginBottom: 12, textAlign: "left" }}>
                    {recsSelecionados.map((r) => (
                      <Typography key={r.id} variant="caption" display="block">
                        {r.numero} · {labels[r.modelo] || r.modelo} · {r.emitenteNome || "—"}
                      </Typography>
                    ))}
                  </div>
                  <div style={{
                    background: sw.bg, border: "1px solid #999", borderRadius: 6,
                    padding: "14px 0", fontWeight: 700, letterSpacing: 2, marginBottom: 12,
                  }}>
                    PAPEL {sw.nome}
                  </div>
                  <Typography variant="caption" color="textSecondary">
                    Carregue o papel na impressora e confirme: o lote sai num único PDF,
                    já em ordem de número.
                  </Typography>
                </div>
              );
            })()}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBatchOpen(false)}>Cancelar</Button>
            <Button variant="contained" disabled={imprimindoLote} startIcon={imprimindoLote ? <CircularProgress size={16} /> : <PrintIcon />}
              onClick={imprimirLote}>
              Papel pronto — imprimir lote
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Cada modelo imprime em papel de outra cor: confirmar dá tempo de trocar. */}
      {printTarget && (
        <Dialog open onClose={() => setPrintTarget(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Confira o papel na impressora</DialogTitle>
          <DialogContent dividers>
            {(() => {
              const sw = PAPEL_SWATCH[modelsInfo[printTarget.modelo]?.papel] || PAPEL_SWATCH.branco;
              return (
                <div style={{ textAlign: "center" }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <b>{printTarget.numero}</b> · {labels[printTarget.modelo] || printTarget.modelo}
                    <br />
                    {printTarget.emitenteNome || ""} — {printTarget.quantidade} folha(s)
                  </Typography>
                  <div style={{
                    background: sw.bg, border: "1px solid #999", borderRadius: 6,
                    padding: "14px 0", fontWeight: 700, letterSpacing: 2, marginBottom: 12,
                  }}>
                    PAPEL {sw.nome}
                  </div>
                  <Typography variant="caption" color="textSecondary">
                    Coloque o papel certo na impressora e confirme. Vários talões? Imprima
                    um por vez, trocando o papel entre eles.
                  </Typography>
                </div>
              );
            })()}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPrintTarget(null)}>Cancelar</Button>
            <Button variant="contained" startIcon={<PrintIcon />}
              onClick={() => { const r = printTarget; setPrintTarget(null); imprimir(r); }}>
              Papel pronto — imprimir
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {sendTarget && (
        <SendToChatDialog
          open={!!sendTarget}
          onClose={() => setSendTarget(null)}
          title={`Enviar receituário ${sendTarget.numero} no chat`}
          subtitle={sendTarget.emitenteNome || ""}
          ticketId={sendTarget.ticketId}
          sendEndpoint={(tid) => `/receituarios/${sendTarget.id}/send/${tid}`}
        />
      )}
    </Paper>
  );
};

export default Receituarios;
